/**
 * StreakWidget — Displays streak status on the Overview page.
 *
 * Shows: current streak count, motivational message, best streak,
 * budget compliance badge, and a 30-day activity heatmap.
 *
 * Renders inside a Card on Overview. Self-contained — reads all data
 * from useApp() and the streakEngine.
 */
import { useMemo } from "react";
import { Card } from '../../components/Card';
import { useApp } from '../../hooks/useApp';
import {
  STREAK_DEFAULTS,
  getStreakMessage,
  getStreakTier,
  todayStr,
} from './streakEngine';

export default function StreakWidget() {
  const { s, c } = useApp();
  const streak = s.streak || STREAK_DEFAULTS;
  const { tier, emoji } = getStreakTier(streak.current);
  const message = getStreakMessage(streak);

  /* ─── 30-day heatmap data ─── */
  const heatmap = useMemo(() => {
    const days = [];
    const today = new Date();
    const history = new Set(streak.history || []);
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      days.push({ key, active: history.has(key), isToday: key === todayStr() });
    }
    return days;
  }, [streak.history]);

  const streakColor =
    tier === "legendary" ? "#BF5AF2" :
    tier === "elite" ? "#FF9F0A" :
    tier === "dedicated" ? c.blue :
    c.green;

  return (
    <Card c={c} delay={0.06} sx={{ padding: "20px 24px" }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 16,
      }}>
        {/* Left: Streak count + message */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10, marginBottom: 8,
          }}>
            {/* Streak number with glow */}
            <div style={{
              fontSize: 32, fontWeight: 800, letterSpacing: "-0.04em",
              color: streak.current > 0 ? streakColor : c.muted,
              lineHeight: 1,
              textShadow: streak.current >= 7
                ? `0 0 20px ${streakColor}40`
                : "none",
            }}>
              {streak.current}
            </div>
            <div>
              <div style={{
                fontSize: 11, fontWeight: 600, color: c.muted,
                letterSpacing: "0.08em", textTransform: "uppercase",
              }}>
                Day Streak {emoji}
              </div>
              {streak.best > streak.current && streak.best > 0 && (
                <div style={{
                  fontSize: 11, color: c.muted, opacity: 0.6, marginTop: 1,
                }}>
                  Best: {streak.best} days
                </div>
              )}
            </div>
          </div>
          <div style={{
            fontSize: 13, color: c.muted, fontWeight: 500, lineHeight: 1.4,
          }}>
            {message}
          </div>

          {/* Budget compliance badge */}
          {streak.budgetStreak > 0 && (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              marginTop: 10, padding: "4px 12px", borderRadius: 16,
              background: s.dark ? "rgba(48,209,88,0.10)" : "rgba(48,209,88,0.06)",
              fontSize: 11, fontWeight: 600, color: c.green,
            }}>
              🎯 {streak.budgetStreak}-day budget streak
            </div>
          )}
        </div>

        {/* Right: 30-day heatmap */}
        <div style={{ flexShrink: 0 }}>
          <div style={{
            fontSize: 10, color: c.muted, fontWeight: 600,
            letterSpacing: "0.06em", textTransform: "uppercase",
            marginBottom: 8, textAlign: "right",
          }}>
            Last 30 Days
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(10, 1fr)",
            gridTemplateRows: "repeat(3, 1fr)",
            gap: 3,
          }}>
            {heatmap.map((day) => (
              <div
                key={day.key}
                title={day.key}
                style={{
                  width: 12, height: 12, borderRadius: 3,
                  background: day.active
                    ? streakColor
                    : s.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
                  opacity: day.active ? (day.isToday ? 1 : 0.7) : 0.4,
                  transition: "all 0.2s ease",
                  ...(day.isToday ? {
                    boxShadow: day.active ? `0 0 6px ${streakColor}60` : "none",
                    border: `1px solid ${day.active ? streakColor : c.border}`,
                  } : {}),
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
